import React, { useState, useEffect, useMemo } from "react";
import {
  MDBBox,
  MDBBtn,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
} from "mdbreact";
import { 
  Modal, 
  Switch, 
  Select, 
  DatePicker, 
  Button, 
  Input, 
  message, 
  Tooltip, 
  Avatar,
  Tag,
  Popconfirm
} from "antd";
import { 
  CopyOutlined, 
  ReloadOutlined, 
  LinkOutlined,
  EyeOutlined,
  EditOutlined,
  MessageOutlined,
  LockOutlined,
  UserOutlined,
  TeamOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  MailOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { url as API_URL, tokenRef } from "api";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { filterPeopleOption, renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import {
  peoplePickerOptions, flatOptions, emailValue, companyPickValue, expandCompanyPicks, companyOptionLabelWithCount,
} from "components/PeoplePicker/peoplePickerOptions";
import {
  updateSharingSettings,
  regenerateShareToken,
  getShareUrl,
  copyShareUrl,
  revokeAllGuests,
  // PURPOSE-BUILT AND, UNTIL NOW, CALLED BY NOTHING. Both take
  // (issueId, collaboratorId, ..., isGuest) and both hit routes that
  // actually write guestCollaborators - which PUT /issue/:id/sharing
  // does not. See the two handlers below.
  removeCollaborator,
  updateCollaboratorPermission
} from "api/issue/issueApi";
import { eventTrack } from "helpers/ga4Helper";
import moment from "moment";
import "./ShareIssueModal.scss";

const { Option } = Select;

export default function ShareIssueModal({
  visible,
  issue,
  onClose,
  onUpdate,
}) {
  // Link settings state
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [publicEnabled, setPublicEnabled] = useState(false);
  const [permission, setPermission] = useState("view");
  const [requiresGuestInfo, setRequiresGuestInfo] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  
  // Collaborator management state (like ShareModal.jsx)
  const [btn, setBtn] = useState("Guests"); // "Guests" | "Users"
  const [search, setSearch] = useState("");
  const [revokingAll, setRevokingAll] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const history = useHistory();

  // INVITE SOMEONE (owner, 2026-10-28: sharing "is not showing available
  // partners in the search box dropdown, and for guests its not offering
  // name and email"). The only box here used to filter the people ALREADY
  // on the share, so nobody new could ever be found or added
  // (shareRecipients.js describes it and was never wired in). Same picker
  // as every other share window (components/Share/ShareResourceModal):
  // the project roster, or any typed address - and a typed stranger is
  // asked for a name. Sent through POST /issue/:id/send-link, which gives
  // each person their own link.
  const [roster, setRoster] = useState([]);
  const [picked, setPicked] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [inviting, setInviting] = useState(false);
  const rosterProject = String(issue?.projectId?._id || issue?.projectId || "");
  useEffect(() => {
    if (!visible || !rosterProject) return;
    getParticipantsRoster(rosterProject)
      .then((r) => setRoster(Array.isArray(r) ? r : []))
      .catch(() => setRoster([]));
  }, [visible, rosterProject]);
  const pickerOptions = useMemo(() => peoplePickerOptions(roster, {
    personValue: emailValue,
    companyValue: companyPickValue,
    companyOptionLabel: companyOptionLabelWithCount,
  }), [roster]);
  const knownEmails = useMemo(
    () => new Set(flatOptions(pickerOptions).filter((o) => o.person).map((o) => o.value)),
    [pickerOptions]
  );
  const strangers = picked.filter((e) => !knownEmails.has(e));
  const handleInvite = async () => {
    const bad = picked.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (bad) return message.warning(`"${bad}" is not an email address`);
    if (!picked.length) return message.warning("Pick someone, or type an email address");
    if (strangers.length === 1 && !guestName.trim()) return message.warning(`Enter a name for ${strangers[0]}`);
    try {
      setInviting(true);
      const names = strangers.length === 1 ? { [strangers[0]]: guestName.trim() } : {};
      const r = await fetch(`${API_URL}/issue/${issue._id}/send-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        body: JSON.stringify({ emails: picked, permission: permission === "view" ? "view" : "edit", names }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) return message.error(b.err || "Could not share the issue");
      if ((b.failed || []).length) message.warning(`${(b.sent || []).length} sent, ${b.failed.length} email(s) could not be sent`);
      else message.success(`Shared with ${(b.sent || []).length} ${(b.sent || []).length === 1 ? "person" : "people"}`);
      setPicked([]); setGuestName("");
      // send-link switches the public link on and adds people; the parent
      // reloads the issue so the list below shows them.
      setPublicEnabled(true);
      onUpdate({ publicAccess: { ...(issue.publicAccess || {}), enabled: true, permission: b.permission || permission } });
      eventTrack("Invite-IssueShare-Action", "Invite-IssueShare", issue?.title, "+1", false, "+1");
    } catch (e) {
      message.error("Could not share the issue");
    } finally {
      setInviting(false);
    }
  };

  useEffect(() => {
    if (issue) {
      setShareUrl(getShareUrl(issue.shareToken));
      setPublicEnabled(issue.publicAccess?.enabled || false);
      setPermission(issue.publicAccess?.permission || "view");
      setRequiresGuestInfo(issue.publicAccess?.requiresGuestInfo !== false);
      setExpiresAt(issue.publicAccess?.expiresAt ? moment(issue.publicAccess.expiresAt) : null);
    }
  }, [issue]);

  const handleCopyLink = async () => {
    try {
      await copyShareUrl(issue.shareToken);
      message.success("Link copied to clipboard!");
      eventTrack("Copy-IssueLink-Action", "Copy-IssueLink", userInfoRedux?.name, "+1", false, "+1");
    } catch (err) {
      message.error("Failed to copy link");
    }
  };

  const handleRegenerateToken = async () => {
    try {
      setRegenerating(true);
      const response = await regenerateShareToken(issue._id);
      setShareUrl(response.shareUrl);
      message.success("Share link regenerated! Previous links are now invalid.");
      onUpdate({ shareToken: response.shareToken });
      eventTrack("Regenerate-IssueToken-Action", "Regenerate-IssueToken", userInfoRedux?.name, "+1", false, "+1");
    } catch (err) {
      message.error("Failed to regenerate link");
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      
      const settings = {
        publicAccess: {
          enabled: publicEnabled,
          permission,
          requiresGuestInfo,
          expiresAt: expiresAt?.toISOString() || null,
        },
      };

      await updateSharingSettings(issue._id, settings);
      
      message.success("Sharing settings updated!");
      onUpdate({ publicAccess: settings.publicAccess });
      eventTrack("Update-IssueSharing-Action", "Update-IssueSharing", userInfoRedux?.name, "+1", false, "+1");
    } catch (err) {
      message.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  // Remove a single collaborator.
  //
  // THIS REPORTED SUCCESS AND REMOVED NOBODY. It used to PUT the whole array
  // to /issue/:id/sharing, and that route destructures ONLY
  // `{ publicAccess, collaborators }` (issue.js:3899) - guestCollaborators is
  // never read, never written, and is not in the response. So removing a GUEST
  // returned 200, this modal updated its own state, the guest vanished from the
  // screen AND KEPT ACCESS. Measured on staging before this was written: two
  // guests before, 200 {"success":true}, two guests after.
  // (Revoke-all was never affected - it calls revokeAllGuests, which has a real
  // route. It was individual removal only, which is why the button that looked
  // most careful was the one that lied.)
  //
  // DELETE /issue/:id/collaborator/:collaboratorId handles users AND guests and
  // was already written, already gated with ISSUE_SHARE, and called by nothing.
  // It also ends a whole-array PUT, which raced anybody else editing the same
  // issue: two people removing two different collaborators each sent a full
  // list, and the second overwrote the first.
  const handleRemoveCollaborator = async (collaboratorId, isGuest) => {
    setRemovingId(collaboratorId);
    try {
      await removeCollaborator(issue._id, collaboratorId, isGuest);
      if (isGuest) {
        onUpdate({ guestCollaborators: (issue.guestCollaborators || []).filter(
          gc => (gc.guestId?._id || gc.guestId) !== collaboratorId) });
      } else {
        onUpdate({ collaborators: (issue.collaborators || []).filter(
          c => (c.userId?._id || c.userId) !== collaboratorId) });
      }
      message.success("Access removed");
      eventTrack("Remove-IssueCollaborator-Action", "Remove-IssueCollaborator", userInfoRedux?.name, "+1", false, "+1");
    } catch (err) {
      message.error("Failed to remove access");
    } finally {
      setRemovingId(null);
    }
  };

  // Update collaborator permission.
  //
  // SAME DEFECT, QUIETER: changing a GUEST's permission PUT the whole array to
  // /sharing, which ignores guestCollaborators - so it returned 200 and the
  // guest kept whatever they had. A guest downgraded from edit to view stayed
  // on edit, and the screen said view.
  // PUT /issue/:id/collaborator/:collaboratorId/permission (issue.js:4095) is
  // the route for this and handles both kinds.
  const handleUpdatePermission = async (collaboratorId, newPermission, isGuest) => {
    setUpdatingId(collaboratorId);
    try {
      await updateCollaboratorPermission(issue._id, collaboratorId, newPermission, isGuest);
      if (isGuest) {
        onUpdate({ guestCollaborators: (issue.guestCollaborators || []).map(gc =>
          ((gc.guestId?._id || gc.guestId) === collaboratorId ? { ...gc, permission: newPermission } : gc)) });
      } else {
        onUpdate({ collaborators: (issue.collaborators || []).map(c =>
          ((c.userId?._id || c.userId) === collaboratorId ? { ...c, permission: newPermission } : c)) });
      }
      message.success("Permission updated");
    } catch (err) {
      message.error("Failed to update permission");
    } finally {
      setUpdatingId(null);
    }
  };

  // Revoke all guest access
  const handleRevokeAllGuests = async () => {
    setRevokingAll(true);
    try {
      const response = await revokeAllGuests(issue._id);
      onUpdate({
        guestCollaborators: [],
        shareToken: response.shareToken,
      });
      setShareUrl(getShareUrl(response.shareToken));
      message.success(`All guest access revoked. ${response.revokedCount} guest(s) removed.`);
      eventTrack("Revoke-AllIssueGuests-Action", "Revoke-AllIssueGuests", userInfoRedux?.name, "+1", false, "+1");
    } catch (err) {
      message.error("Failed to revoke guest access");
    } finally {
      setRevokingAll(false);
    }
  };

  // Get filtered collaborators based on current tab and search
  const getFilteredCollaborators = () => {
    const list = btn === "Guests" 
      ? (issue?.guestCollaborators || []).map(gc => ({
          ...gc,
          user: gc.guestId,
          isGuest: true,
          collaboratorId: gc.guestId?._id || gc.guestId,
        }))
      : (issue?.collaborators || []).map(c => ({
          ...c,
          user: c.userId,
          isGuest: false,
          collaboratorId: c.userId?._id || c.userId,
        }));

    return list.filter(item => 
      !search || 
      item.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
      item.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.user?.company?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const filteredCollaborators = getFilteredCollaborators();
  const guestCount = (issue?.guestCollaborators || []).length;
  const userCount = (issue?.collaborators || []).length;

  const permissionOptions = [
    { value: "view", label: "View only", icon: <EyeOutlined /> },
    { value: "comment", label: "Comment", icon: <MessageOutlined /> },
    { value: "edit", label: "Edit", icon: <EditOutlined /> },
  ];

  return (
    <MDBModal toggle={onClose} isOpen={visible} className="big share-issue-modal" centered>
      <MDBModalHeader toggle={onClose} className="d-flex mb-0 pb-2">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LinkOutlined style={{ color: "#3B82F6" }} />
          Shared Access
        </div>
      </MDBModalHeader>
      <MDBModalBody className="pt-1">
        {/* Link Section */}
        <div className="share-link-section mb-3">
          <label className="section-label">Share Link</label>
          <div className="d-flex" style={{ gap: 8 }}>
            <Input
              value={shareUrl}
              readOnly
              style={{ flex: 1 }}
              prefix={<LinkOutlined style={{ color: "#9CA3AF" }} />}
            />
            <Tooltip title="Copy link">
              <Button icon={<CopyOutlined />} onClick={handleCopyLink} />
            </Tooltip>
            <Tooltip title="Regenerate link (invalidates old links)">
              <Button 
                icon={<ReloadOutlined spin={regenerating} />} 
                onClick={handleRegenerateToken}
                loading={regenerating}
              />
            </Tooltip>
          </div>
        </div>

        {/* Settings Row */}
        <div className="settings-row mb-3">
          <div className="setting-item">
            <span>Public Access</span>
            <Switch checked={publicEnabled} onChange={setPublicEnabled} size="small" />
          </div>
          {publicEnabled && (
            <>
              <div className="setting-item">
                <span>Default Permission</span>
                <Select value={permission} onChange={setPermission} size="small" style={{ width: 100 }}>
                  {permissionOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </div>
              <div className="setting-item">
                <span>Require Info</span>
                <Switch checked={requiresGuestInfo} onChange={setRequiresGuestInfo} size="small" />
              </div>
            </>
          )}
          <Button type="primary" size="small" onClick={handleSaveSettings} loading={loading}>
            Save
          </Button>
        </div>

        {/* Tab Buttons - Like ShareModal.jsx */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center justify-content-between" style={{ width: "50%" }}>
            <MDBBtn
              size="sm"
              color=""
              className={btn === 'Guests' ? 'sharePending-button active-toggle' : 'sharePending-button'}
              onClick={() => setBtn("Guests")}
            >
              Guests ({guestCount})
            </MDBBtn>
            <MDBBtn
              size="sm"
              color=""
              className={btn === 'Users' ? 'sharePending-button active-toggle' : 'sharePending-button'}
              onClick={() => {
                setBtn("Users");
                eventTrack("View-IssueUsers-Action", "View-IssueUsers", issue?.title, "+1", false, "+1");
              }}
            >
              Users ({userCount})
            </MDBBtn>
          </div>
          <h5 className="text-status">Status: {btn}</h5>
        </div>

        {/* Invite: partners on the project, or anyone by email */}
        <div style={{ display: "flex", gap: 8, marginBottom: strangers.length === 1 ? 8 : 12 }}>
          <Select
            mode="tags"
            style={{ flex: 1 }}
            value={picked}
            onChange={(v) => setPicked(expandCompanyPicks(v, roster)
              .map((x) => String(x).trim().toLowerCase()).filter(Boolean))}
            options={pickerOptions}
            placeholder="Partners on this project, or type any email address"
            tokenSeparators={[",", ";", " "]}
            filterOption={filterPeopleOption}
            optionRender={renderPeopleOption}
            maxTagCount="responsive"
            maxTagPlaceholder={peopleTagPlaceholder(pickerOptions)}
            notFoundContent="No match - type a full email to invite them"
            disabled={inviting}
          />
          <Button type="primary" icon={<MailOutlined />} onClick={handleInvite}
                  loading={inviting} disabled={!picked.length}>
            Send
          </Button>
        </div>
        {strangers.length === 1 && (
          <Input
            style={{ marginBottom: 12 }}
            prefix={<UserOutlined style={{ color: "#9CA3AF" }} />}
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder={`Name for ${strangers[0]} (guest)`}
            disabled={inviting}
          />
        )}

        {/* Filter the people already on the share */}
        <div className="form-group mb-2">
          <input
            type="text"
            className="shared-input-search"
            placeholder="Filter who has access by email, name, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Collaborators Table - Like ShareModal.jsx */}
        <div className="tbody">
          <div className="tr theader">
            <div className="td" style={{ flex: 2 }}>Email</div>
            <div className="td" style={{ flex: 1 }}>Access Options</div>
            <div className="td" style={{ flex: 1 }}>Activity</div>
            <div className="td" style={{ width: 40 }}></div>
          </div>

          {filteredCollaborators.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "#6B7280" }}>
              <UserOutlined style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }} />
              <p style={{ margin: 0 }}>
                {search 
                  ? "No results match your search"
                  : btn === "Guests" 
                    ? "No guest collaborators yet" 
                    : "No user collaborators yet"
                }
              </p>
              {!search && btn === "Guests" && publicEnabled && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#9CA3AF" }}>
                  Share the link to invite guests
                </p>
              )}
            </div>
          ) : (
            filteredCollaborators.map((item) => (
              <div key={item.collaboratorId} className="tr" style={{ alignItems: "center" }}>
                {/* Email/User Info */}
                <div className="td" style={{ flex: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar
                      size={32}
                      src={
                        !item.isGuest && item.user?.profilePhoto
                          ? (item.user.profilePhoto.startsWith("http")
                              ? item.user.profilePhoto
                              : `${API_URL}/${item.user.profilePhoto}`)
                          : undefined
                      }
                      style={{
                        backgroundColor: item.isGuest
                          ? (item.user?.avatarColor || "#8B5CF6")
                          : "#3B82F6",
                        flexShrink: 0
                      }}
                    >
                      {item.user?.name?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                    <div style={{ minWidth: 0, overflow: "hidden" }}>
                      <div style={{ fontWeight: 500, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                        {item.user?.name || "Unknown"}
                        {item.isGuest && <Tag style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>Guest</Tag>}
                      </div>
                      <div style={{ fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.user?.email || "-"}
                      </div>
                      {item.user?.company && (
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                          <BankOutlined style={{ fontSize: 10, marginRight: 4 }} />
                          {item.user.company}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Access Options */}
                <div className="td" style={{ flex: 1 }}>
                  <Select
                    value={item.permission}
                    onChange={(value) => handleUpdatePermission(item.collaboratorId, value, item.isGuest)}
                    size="small"
                    style={{ width: 100 }}
                    loading={updatingId === item.collaboratorId}
                  >
                    {permissionOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {opt.icon} {opt.label}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Activity */}
                <div className="td" style={{ flex: 1, fontSize: 12, color: "#6B7280" }}>
                  {item.isGuest ? (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <HistoryOutlined style={{ fontSize: 10 }} />
                        {item.user?.changesCount || 0} changes
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <ClockCircleOutlined style={{ fontSize: 10 }} />
                        {item.user?.lastAccessed 
                          ? moment(item.user.lastAccessed).fromNow() 
                          : "Never"
                        }
                      </div>
                    </>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <ClockCircleOutlined style={{ fontSize: 10 }} />
                      Added {item.addedAt ? moment(item.addedAt).format("MMM D, YYYY") : "-"}
                    </div>
                  )}
                </div>

                {/* Analytics Button (registered users only) */}
                {!item.isGuest && item.collaboratorId && (
                  <div className="td" style={{ width: 36, textAlign: "center" }}>
                    <button
                      onClick={() => history.push(`/analytics/user/${item.collaboratorId}`)}
                      title="View analytics"
                      style={{
                        width: 26, height: 26, borderRadius: 4,
                        background: "#F3F4F6", border: "1px solid #E5E7EB",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3A5765" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3v18h18"/>
                        <path d="M7 14l3-3 4 4 5-7"/>
                      </svg>
                    </button>
                  </div>
                )}

                {/* Delete Button */}
                <div className="td" style={{ width: 40, textAlign: "center" }}>
                  <Popconfirm
                    title="Remove access?"
                    description={`${item.user?.name || "This person"} will no longer have access.`}
                    onConfirm={() => handleRemoveCollaborator(item.collaboratorId, item.isGuest)}
                    okText="Remove"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <div
                      className="tr-delete"
                      style={{
                        cursor: removingId === item.collaboratorId ? "wait" : "pointer",
                        opacity: removingId === item.collaboratorId ? 0.5 : 1
                      }}
                    />
                  </Popconfirm>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer with Revoke All button */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          marginTop: 16,
          paddingTop: 12,
          borderTop: "1px solid #E5E7EB"
        }}>
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            Total: {guestCount + userCount} collaborator{guestCount + userCount !== 1 ? "s" : ""}
          </span>
          {guestCount > 0 && (
            <Popconfirm
              title="Revoke all guest access?"
              description="This will remove all guests and generate a new share link."
              onConfirm={handleRevokeAllGuests}
              okText="Revoke All"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: revokingAll }}
            >
              <Button danger size="small" loading={revokingAll}>
                Revoke All Guests
              </Button>
            </Popconfirm>
          )}
        </div>
      </MDBModalBody>
    </MDBModal>
  );
}
